import { useEffect, useMemo, useState, useContext } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { CreditCard, Loader2, AlertCircle, Sparkles, Check, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { AuthContext } from '../contexts/auth';
import { createSubscriptionIntent, finalizeUpgrade } from '../api/subscription';
import { getProfile } from '../api/profile';

const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = PUBLISHABLE_KEY ? loadStripe(PUBLISHABLE_KEY) : null;

function flattenBullets(bullets = []) {
  const out = [];
  bullets.forEach((b) => {
    if (typeof b === 'string') {
      out.push(b);
    } else if (b && typeof b === 'object') {
      out.push(b.text);
      (b.subItems || []).forEach((s) => out.push(s));
    }
  });
  return out;
}

function ComparePlansView({ freeBullets, proBullets, onContinue, onCancel }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            {t('currentPlanFree')}
          </p>
          <p className="mt-0.5 text-base font-bold text-slate-700">Free</p>
          <ul className="mt-3 space-y-1.5 text-xs text-slate-600">
            {freeBullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border-2 border-indigo-300 bg-gradient-to-br from-indigo-50 to-blue-50 p-4 shadow-sm">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
            {t('newPlanPro')}
          </p>
          <p className="mt-0.5 text-base font-bold text-slate-900">Pro</p>
          <ul className="mt-3 space-y-1.5 text-xs text-slate-700">
            {proBullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                <span className="font-medium">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Button
        type="button"
        onClick={onContinue}
        className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800"
      >
        {t('continueToPayment')}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}

function UpgradeForm({ onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;

    setSubmitting(true);
    setErrorMessage('');
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (error) {
        setErrorMessage(error.message || t('paymentDeclined'));
        return;
      }
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        await onPaid();
      } else {
        toast.info(t('paymentProcessing'));
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(err?.message || t('paymentUnexpectedError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={{ layout: 'tabs' }} />

      {errorMessage && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800"
      >
        {submitting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <CreditCard className="w-4 h-4 mr-2" />
            {t('payAndFinish')}
          </>
        )}
      </Button>
    </form>
  );
}

async function refreshUserProfile({ user, setUser, storageUser }) {
  if (!user?.id) return;
  const fresh = await getProfile(user.id);
  const updatedUser = { ...user, ...fresh };
  setUser(updatedUser);
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const remember = !!localStorage.getItem('token');
  storageUser(updatedUser, token, remember);
}

export function UpgradeProModal({ open, onOpenChange, onSuccess }) {
  const { t } = useTranslation();
  const { t: tLanding } = useTranslation('landing');
  const auth = useContext(AuthContext);
  const [step, setStep] = useState('compare');
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setStep('compare');
      setPending(null);
      setError('');
      setLoading(false);
    }
  }, [open]);

  const freeBullets = useMemo(() => {
    const arr = tLanding('plans.items.0.bullets', { returnObjects: true });
    return Array.isArray(arr) ? flattenBullets(arr) : [];
  }, [tLanding]);

  const proBullets = useMemo(() => {
    const arr = tLanding('plans.items.1.bullets', { returnObjects: true });
    return Array.isArray(arr) ? flattenBullets(arr) : [];
  }, [tLanding]);

  const handleContinue = async () => {
    setStep('payment');
    setLoading(true);
    setError('');
    try {
      const data = await createSubscriptionIntent();

      // Caso especial: user tinha cancel_at_period_end agendado e o backend
      // reativou direto (sem nova cobranca). Nao precisa abrir Elements.
      if (data.action === 'reactivated') {
        await refreshUserProfile(auth);
        toast.success(t('subscriptionReactivated'));
        onOpenChange?.(false);
        onSuccess?.();
        return;
      }

      setPending({
        subscriptionId: data.subscriptionId,
        clientSecret: data.clientSecret,
      });
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || t('paymentIntentError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePaid = async () => {
    try {
      if (pending?.subscriptionId) {
        await finalizeUpgrade(pending.subscriptionId);
      }
      await refreshUserProfile(auth);
      toast.success(t('welcomePro'));
      onOpenChange?.(false);
      onSuccess?.();
    } catch (err) {
      console.error('falha confirmando upgrade pos-pagamento', err);
      toast.error(err?.response?.data?.error || t('paymentUnexpectedError'));
      onOpenChange?.(false);
    }
  };

  const options = useMemo(
    () => (pending?.clientSecret ? { clientSecret: pending.clientSecret, appearance: { theme: 'stripe' } } : null),
    [pending?.clientSecret]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            <DialogTitle>
              {step === 'compare' ? t('comparePlansTitle') : t('upgradeModalTitle')}
            </DialogTitle>
          </div>
          {step === 'payment' && (
            <p className="text-sm text-slate-500">{t('paymentStepDescription')}</p>
          )}
        </DialogHeader>

        {step === 'compare' ? (
          <ComparePlansView
            freeBullets={freeBullets}
            proBullets={proBullets}
            onContinue={handleContinue}
            onCancel={() => onOpenChange?.(false)}
          />
        ) : !stripePromise ? (
          <div className="p-4 text-center text-red-600 text-sm">
            REACT_APP_STRIPE_PUBLISHABLE_KEY não configurada.
          </div>
        ) : loading ? (
          <div className="p-6 flex flex-col items-center gap-3 text-slate-600">
            <Loader2 className="w-6 h-6 animate-spin" />
            <p className="text-sm">{t('preparingPayment')}</p>
          </div>
        ) : error || !pending?.clientSecret ? (
          <div className="space-y-2">
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error || t('paymentIntentError')}</span>
            </div>
          </div>
        ) : (
          <Elements stripe={stripePromise} options={options}>
            <UpgradeForm onPaid={handlePaid} />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default UpgradeProModal;
